import cn from 'classnames';
import { Link } from 'gatsby';
import React, { FC } from 'react';
import { segmentTrack } from 'src/utils/analytics';
import { ACTION } from 'src/utils/analytics/constants';
import ColumnCta from './ColumnCta';
import MenuCta from './MenuCta';
import './style.scss';

export interface Item {
  __typename: string;
  id: string;
  title?: string;
  ctaDescription?: {
    raw: string;
  };
  itemId?: string;
  buttonText?: string;
  buttonLink?: string;
  link?: string;
  text?: string;
  icon?: {
    url: string;
    title: string;
  } | null;
  badgeText?: null | string;
}

export interface GridColumnItem {
  title: string;
  columnWidth: number;
  items: Item[];
  contentful_id: string;
  backgroundColor?: boolean;
  hideOnMobile?: boolean;
  highlight?: boolean;
  target?: string;
}
export interface SubMenuProps {
  gridColumnItems: GridColumnItem[];
  gridColumns: number;
  menuCta?: {
    title: string;
    buttonLink: string;
    buttonText: string;
    buttonId?: string;
    ctaDescription: {
      raw: string;
    };
  };
  navDropdownId?: string;
  locale?: string;
}

const SubMenu: FC<SubMenuProps> = props => {
  const { gridColumnItems, gridColumns, menuCta, navDropdownId, locale } =
    props;

  return (
    <>
      <div
        id="sub-menu"
        className="h-2 z-0 cursor-default bg-transparent w-full absolute -bottom-2 left-0 right-0 max-w-[980px] mx-auto"
      />
      <div
        id="sub-menu"
        className={cn(
          'bg-white sub-menu lg:absolute top-full w-full min-h-[250px] lg:mt-[6px] rounded-xl duration-150 overflow-hidden lg:border lg:border-gray-4 lg:shadow-lg max-w-[980px] mx-auto left-0 right-0',
          navDropdownId === 'nav-media' &&
            'sm:max-w-fit sm:min-h-fit sm:left-[150px]'
        )}
      >
        <ul className={cn('flex lg:flex-row flex-col w-full h-full')}>
          {gridColumnItems?.map((column, i) => {
            return (
              <li
                key={column.contentful_id}
                className={cn(
                  'p-2 lg:p-5 text-gray-3-dark cursor-auto',
                  `lg:w-1/${gridColumns}`,
                  column.backgroundColor === true && 'lg:bg-[#FCFCFF]',
                  column.highlight === true &&
                    'lg:max-w-[245px] order-last lg:order-first',
                  column.hideOnMobile === true && 'hidden lg:block'
                )}
              >
                {column.target ? (
                  <Link
                    className="pb-1 lg:border-b-2 lg:border-gray-5-light w-full block hover:text-indigo-1"
                    to={column.target}
                  >
                    {column.title}
                  </Link>
                ) : (
                  navDropdownId !== 'nav-services' && (
                    <span className="pb-1 lg:border-b-2 text-gray-2-light lg:border-gray-5-light w-full block">
                      {column.title}
                    </span>
                  )
                )}
                <ul className="lg:pt-5">
                  {column?.items?.map((item, i) => {
                    return (
                      <>
                        <Link
                          id={item?.itemId}
                          to={item.link} 
                          onClick={() => segmentTrack(ACTION.NaviClicked, {label: item.title || item.text})}
                        >
                          <li
                            key={item.id}
                            className={cn(
                              'flex items-start nav-link pr-5 flex-col justify-between cursor-pointer [&>div>svg]:hover:block group text-gray-3-dark font-medium text-lg py-3 rounded-lg px-3 relative',
                              column.highlight === true &&
                                '!text-2xl !text-indigo-3-dark !font-semibold !items-center',
                              item.__typename == 'ContentfulBlogCta'
                                ? 'menu-cta !cursor-default'
                                : 'hover:bg-gray-6-light'
                            )}
                          >
                            {item.__typename === 'ContentfulBlogCta' ? (
                              <ColumnCta
                                title={item.title}
                                description={item?.ctaDescription?.raw}
                                buttonLink={item.buttonLink}
                                buttonText={item.buttonText}
                              />
                            ) : (
                              <>
                                <div className="flex flex-row items-center w-full justify-between">
                                  <div
                                    className={cn(
                                      'flex flex-row items-start',
                                      column.highlight === true &&
                                        '!items-center'
                                    )}
                                  >
                                    {item.icon && (
                                      <img
                                        className="mr-2"
                                        width={
                                          column.highlight === true ? 50 : 24
                                        }
                                        height={
                                          column.highlight === true ? 38 : 24
                                        }
                                        src={item.icon.url}
                                        alt={item.icon.title}
                                      />
                                    )}
                                    <span
                                      className={cn(
                                        'flex flex-col mr-1',
                                        column.highlight === true && 'pl-5'
                                      )}
                                    >
                                      {item.text || item.title}
                                    </span>
                                  </div>
                                </div>
                                {item?.description && (
                                  <span className="text-gray-2-dark text-base block ml-[30px]">
                                    {item?.description.description}
                                  </span>
                                )}
                              </>
                            )}
                          </li>
                        </Link>
                      </>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
        <MenuCta menuCta={menuCta} />
      </div>
    </>
  );
};

export default SubMenu;
